import { useAuthState } from '@/app/providers/auth'
import { getDeliveryDetailsForMixpanel, useMixPanel } from '@/helpers/mixpanelConfig'
import { recordPopularCarouselItem } from '@/helpers/mixpanelPopularCarouselAttribution'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'

import { useCallback } from 'react'

import type { UserBasketSettings } from '../../common/domain/user-basket-settings'

export type ItemAddSource = 'PopularItemsCarousel' | 'SupplierMenu'

interface ItemAddedToBasketContext {
  vendor: {
    id: string
    companyName: string
    isPantry: boolean | null
  } | null
  orderSettings: UserBasketSettings
}

interface ItemAddedToBasketEventParams {
  itemId: string
  itemName: string
  quantity: number
  source: ItemAddSource
  isCateringTeamOrder?: boolean
}

export const useMixpanelItemAddedToBasket = ({
  vendor,
  orderSettings,
}: ItemAddedToBasketContext) => {
  const mixPanel = useMixPanel()
  const tenant = useTenant()
  const authState = useAuthState()
  const itemAddedToBasketEnabled = useFeatureFlag(SPLITS.MIXPANEL_ITEM_ADDED_TO_BASKET)

  const trackItemAddedToBasket = useCallback(
    ({ itemId, itemName, quantity, source, isCateringTeamOrder }: ItemAddedToBasketEventParams) => {
      if (!vendor) return

      if (source === 'PopularItemsCarousel') recordPopularCarouselItem(itemId)

      if (!itemAddedToBasketEnabled) return

      try {
        const deliveryDetails = getDeliveryDetailsForMixpanel(orderSettings)
        mixPanel?.track('Item Added to Basket', {
          name: 'Item Added to Basket',
          ...deliveryDetails,
          itemID: itemId,
          itemName,
          quantity,
          source,
          supplierType: vendor.isPantry ? 'Pantry' : 'Catering',
          address: orderSettings.location,
          customerAccount:
            authState.type === 'authenticated'
              ? `${authState.user?.id}-${authState.user.account?.name}`
              : 'guest',
          date: orderSettings.date,
          headCount: orderSettings.headCount,
          tenant: tenant?.name,
          vendor: vendor.companyName,
          vendorId: vendor.id,
          teamOrder: Boolean(isCateringTeamOrder),
        })
      } catch (e) {
        console.error('Mixpanel tracking failed', e)
      }
    },
    [itemAddedToBasketEnabled, mixPanel, authState, tenant, vendor, orderSettings],
  )

  return { trackItemAddedToBasket }
}
